import { formAtom } from "@impero-com/common/utils/form/formAtom";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { focusAtom } from "jotai-optics";
import * as Yup from "yup";

import { SavedBlob } from "api/generated/model";
import { Status, UploaderFile } from "components/fileUploader/types";
import { formatBlobUrl } from "components/fileUploader/utils";

const validStatusStrings: Status[] = ["success", "done", "removed", "missing"];

export interface BlobData {
    blobIds: string[];
    blobs: (SavedBlob & { keyId?: string })[];
}

function updateBlobIds(
    blobData: BlobData,
    uploaderFiles: UploaderFile[]
): BlobData {
    let ids = [...blobData.blobIds];
    let blobs = [...blobData.blobs];
    uploaderFiles.forEach((file) => {
        switch (file.status) {
            case "done":
            case "success": {
                if (!ids.includes(file.id)) {
                    ids = ids.filter((id) => id !== file.keyId);
                    blobs = blobs.filter((blob) => blob.id !== file.keyId);
                    ids.push(file.id);
                    blobs.push({
                        fileName: file.fileName,
                        fileSize: file.fileSize,
                        fileType: file.fileType,
                        id: file.id,
                        lastModified: file.lastModified,
                        keyId: file.keyId,
                    });
                }
                break;
            }
            case "uploading": {
                if (!ids.includes(file.keyId)) {
                    ids.push(file.keyId);
                    blobs.push({
                        fileName: file.fileName,
                        fileSize: file.fileSize,
                        fileType: file.fileType,
                        id: file.keyId,
                        lastModified: file.lastModified,
                        keyId: file.keyId,
                    });
                }
                break;
            }
            case "removed": {
                ids = ids.filter((id) => id !== file.oldId);
                blobs = blobs.filter((blob) => blob.id !== file.oldId);
                break;
            }
            default:
                break;
        }
    });

    return {
        blobIds: [...ids],
        blobs: [...blobs],
    };
}

function blobIdsToUploaderFiles(blobData: BlobData): UploaderFile[] {
    return blobData.blobIds.map((id): UploaderFile => {
        const blob = blobData.blobs.find((blob) => blob.id === id);
        if (blob) {
            return {
                status: "done",
                id,
                keyId: blob.keyId ?? blob.id,
                url: formatBlobUrl(blob.id),
                fileName: blob.fileName,
                fileSize: blob.fileSize,
                fileType: blob.fileType,
                lastModified: blob.lastModified,
            };
        } else {
            return {
                status: "missing",
                keyId: id,
                id,
                error: "Could not find file in blobs",
                fileName: "Unknown",
                fileSize: 0,
                fileType: "",
                lastModified: "",
            };
        }
    });
}

export function mapBlobDataToUploaderFiles(
    blobDataAtom: FormAtom<BlobData>,
    validationText: string
): FormAtom<UploaderFile[]> {
    const uploaderFilesAtom = focusAtom(blobDataAtom, (o) =>
        o.lens(blobIdsToUploaderFiles, updateBlobIds)
    );

    const savedBlobArraySchema = Yup.array()
        .of(
            Yup.object({
                status: Yup.string()
                    .defined()
                    .oneOf(validStatusStrings, validationText),
            })
        )
        .defined();

    return formAtom(
        uploaderFilesAtom,
        savedBlobArraySchema as unknown as Yup.ISchema<UploaderFile[]>
    );
}
